import React, { useState, useEffect, useRef } from 'react';

// Phones/tablets: keep the heavy canvas effects still (or off) so the hero hologram stays smooth
const isLowPowerScreen = () =>
  typeof window !== 'undefined' && window.matchMedia('(max-width: 768px), (pointer: coarse)').matches;

/* ─── AURORA BACKGROUND (canvas) ────────────── */
export const AuroraBackground = () => {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = () => window.innerWidth;
    const H = () => window.innerHeight;
    // Drawn at 1x on purpose: only soft gradients here, and device resolution
    // (3x on phones) would cost 9x the pixels every frame for no visible gain
    const resize = () => {
      canvas.width = W(); canvas.height = H();
      canvas.style.width = W() + 'px'; canvas.style.height = H() + 'px';
    };
    resize();
    window.addEventListener('resize', resize);

    // Slow-drifting large color blobs
    const BLOBS = [
      { ox: 0.10, oy: 0.08, rgb: '189,147,249', a: 0.09, T: 40000, ph: 0.0 },  // purple — top-left
      { ox: 0.82, oy: 0.20, rgb: '255,121,198', a: 0.07, T: 33000, ph: 2.1 },  // pink — top-right
      { ox: 0.45, oy: 0.58, rgb: '80,250,123',  a: 0.055, T: 52000, ph: 4.0 }, // green — mid
      { ox: 0.55, oy: 0.06, rgb: '139,233,253', a: 0.06, T: 44000, ph: 1.1 },  // cyan — top-center
      { ox: 0.15, oy: 0.68, rgb: '255,85,85',   a: 0.038, T: 61000, ph: 3.3 }, // blood-red — bottom-left
      { ox: 0.78, oy: 0.72, rgb: '98,114,164',  a: 0.045, T: 70000, ph: 5.5 }, // comment-blue — bottom-right
    ];

    const still = isLowPowerScreen();
    let t = 0, animId;
    const draw = () => {
      ctx.fillStyle = '#060810';
      ctx.fillRect(0, 0, W(), H());
      BLOBS.forEach(b => {
        const cx = (b.ox + Math.sin(t / b.T * Math.PI * 2 + b.ph) * 0.16) * W();
        const cy = (b.oy + Math.cos(t / b.T * Math.PI * 2 * 0.65 + b.ph * 1.3) * 0.10) * H();
        const R  = 0.52 * Math.min(W(), H()) * (1 + 0.07 * Math.sin(t / b.T * Math.PI * 4 + b.ph));
        const a  = b.a * (0.75 + 0.25 * Math.sin(t / b.T * Math.PI * 3 + b.ph));
        const g  = ctx.createRadialGradient(cx, cy, 0, cx, cy, R);
        g.addColorStop(0,    `rgba(${b.rgb},${a})`);
        g.addColorStop(0.35, `rgba(${b.rgb},${(a * 0.5).toFixed(3)})`);
        g.addColorStop(0.7,  `rgba(${b.rgb},${(a * 0.15).toFixed(3)})`);
        g.addColorStop(1,    'rgba(0,0,0,0)');
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, W(), H());
      });
      t += 16;
      if (!still) animId = requestAnimationFrame(draw);
    };
    draw();
    // A still aurora still has to be repainted after a resize (which clears the canvas)
    const onResize = () => { if (still) draw(); };
    window.addEventListener('resize', onResize);
    return () => { cancelAnimationFrame(animId); window.removeEventListener('resize', resize); window.removeEventListener('resize', onResize); };
  }, []);
  return <canvas ref={canvasRef} style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none' }} />;
};

/* ─── STAR FIELD (canvas) ────────────────────── */
export const StarField = () => {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = Math.min(window.devicePixelRatio || 1, 2); // 2x is sharp enough; 3x phones would draw 2.25x the pixels
    const W = () => window.innerWidth;
    const H = () => window.innerHeight;
    const resize = () => {
      canvas.width = W() * dpr; canvas.height = H() * dpr;
      canvas.style.width = W() + 'px'; canvas.style.height = H() + 'px';
      ctx.scale(dpr, dpr);
    };
    resize();
    window.addEventListener('resize', resize);

    const stars = Array.from({ length: 200 }, () => ({
      x:   Math.random(),
      y:   Math.random() * 0.88,
      r:   0.3 + Math.random() ** 2 * 2.2,
      ph:  Math.random() * Math.PI * 2,
      spd: 0.25 + Math.random() * 1.6,
      rgb: Math.random() < 0.22 ? '189,147,249' : Math.random() < 0.15 ? '139,233,253' : '248,248,242',
      base: 0.08 + Math.random() * 0.58,
    }));

    let t = 0, animId;
    const draw = () => {
      ctx.clearRect(0, 0, W(), H());
      stars.forEach(s => {
        const a = s.base * (0.35 + 0.65 * Math.abs(Math.sin(t * s.spd + s.ph)));
        ctx.beginPath();
        ctx.arc(s.x * W(), s.y * H(), s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${s.rgb},${a.toFixed(3)})`;
        ctx.fill();
        // Soft halo for brighter stars
        if (s.r > 1.1 && a > 0.45) {
          const g = ctx.createRadialGradient(s.x*W(), s.y*H(), 0, s.x*W(), s.y*H(), s.r * 5.5);
          g.addColorStop(0, `rgba(${s.rgb},${(a * 0.18).toFixed(3)})`);
          g.addColorStop(1, 'rgba(0,0,0,0)');
          ctx.fillStyle = g;
          ctx.beginPath();
          ctx.arc(s.x*W(), s.y*H(), s.r * 5.5, 0, Math.PI * 2);
          ctx.fill();
        }
      });
      t += 0.014;
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(animId); window.removeEventListener('resize', resize); };
  }, []);
  return <canvas ref={canvasRef} style={{ position: 'fixed', inset: 0, zIndex: 1, pointerEvents: 'none' }} />;
};

/* ─── PARTICLE CONSTELLATION (canvas) ───────── */
export const ParticleNet = () => {
  const canvasRef = useRef(null);
  const [enabled] = useState(() => !isLowPowerScreen());

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const resize = () => {
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = window.innerWidth + 'px';
      canvas.style.height = window.innerHeight + 'px';
      ctx.scale(dpr, dpr);
    };
    resize();
    window.addEventListener('resize', resize);

    const W = () => window.innerWidth;
    const H = () => window.innerHeight;

    const COLORS = ['#bd93f9', '#ff79c6', '#50fa7b', '#8be9fd', '#f1fa8c'];
    // Fewer particles on small screens — less clutter, lighter on phones
    const COUNT = Math.min(70, Math.round((W() * H()) / 9000));

    const pts = Array.from({ length: COUNT }, () => ({
      x: Math.random() * W(),
      y: Math.random() * H(),
      vx: (Math.random() - 0.5) * 0.35,
      vy: (Math.random() - 0.5) * 0.35,
      r: 1 + Math.random() * 2,
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
      alpha: 0.3 + Math.random() * 0.5,
    }));

    let mx = -999, my = -999;
    const onMove = e => { mx = e.clientX; my = e.clientY; };
    window.addEventListener('mousemove', onMove);

    let animId;
    const draw = () => {
      ctx.clearRect(0, 0, W(), H());

      // Move particles
      pts.forEach(p => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > W()) p.vx *= -1;
        if (p.y < 0 || p.y > H()) p.vy *= -1;

        // Mouse repulsion
        const dx = p.x - mx, dy = p.y - my;
        const d = Math.hypot(dx, dy);
        if (d < 120) { p.x += dx * 0.025; p.y += dy * 0.025; }

        // Draw dot with glow
        ctx.save();
        ctx.shadowBlur = 8;
        ctx.shadowColor = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = p.color + Math.floor(p.alpha * 255).toString(16).padStart(2, '0');
        ctx.fill();
        ctx.restore();
      });

      // Draw connections
      for (let i = 0; i < COUNT; i++) {
        for (let j = i + 1; j < COUNT; j++) {
          const dx = pts[i].x - pts[j].x, dy = pts[i].y - pts[j].y;
          const d = Math.hypot(dx, dy);
          if (d < 140) {
            const a = (1 - d / 140) * 0.18;
            const grd = ctx.createLinearGradient(pts[i].x, pts[i].y, pts[j].x, pts[j].y);
            grd.addColorStop(0, pts[i].color + Math.floor(a * 255).toString(16).padStart(2, '0'));
            grd.addColorStop(1, pts[j].color + Math.floor(a * 255).toString(16).padStart(2, '0'));
            ctx.beginPath();
            ctx.moveTo(pts[i].x, pts[i].y);
            ctx.lineTo(pts[j].x, pts[j].y);
            ctx.strokeStyle = grd;
            ctx.lineWidth = 0.6;
            ctx.stroke();
          }
        }
      }

      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', onMove);
    };
  }, []);

  if (!enabled) return null;
  return (
    <canvas ref={canvasRef} style={{
      position: 'fixed', inset: 0, zIndex: 3,
      pointerEvents: 'none', opacity: 0.5,
    }} />
  );
};

/* ─── SPIDER WEB SVG ─────────────────────────── */
const SpiderWeb = ({ size = 90 }) => {
  const cx = size / 2, R = size / 2 - 2;
  const ARMS = 8, RINGS = 4;
  const pt = (r, i) => {
    const a = (i * (360 / ARMS) - 90) * Math.PI / 180;
    return [cx + Math.cos(a) * r, cx + Math.sin(a) * r];
  };
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} fill="none">
      {/* Radial arms */}
      {Array.from({ length: ARMS }, (_, i) => {
        const [x, y] = pt(R, i);
        return <line key={i} x1={cx} y1={cx} x2={x} y2={y} stroke="#bd93f9" strokeWidth="0.8" strokeOpacity="0.75" />;
      })}
      {/* Concentric rings */}
      {Array.from({ length: RINGS }, (_, ri) => {
        const r = R * ((ri + 1) / RINGS);
        const pts = Array.from({ length: ARMS }, (_, i) => pt(r, i).join(',')).join(' ');
        return <polygon key={ri} points={pts} fill="none" stroke="#bd93f9" strokeWidth="0.7" strokeOpacity={0.7 - ri * 0.12} />;
      })}
      {/* Silk highlight threads */}
      {Array.from({ length: ARMS }, (_, i) => {
        const [x1, y1] = pt(R * 0.3, i);
        const [x2, y2] = pt(R * 0.7, (i + 1) % ARMS);
        return <line key={`s${i}`} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#ff79c6" strokeWidth="0.5" strokeOpacity="0.25" />;
      })}
      {/* Spider in center */}
      <circle cx={cx} cy={cx} r="3" fill="#ff79c6" opacity="0.9" style={{ filter: 'drop-shadow(0 0 4px #ff79c6)' }} />
      <circle cx={cx} cy={cx} r="1.2" fill="#0d0e12" opacity="0.8" />
    </svg>
  );
};

/* ─── CUSTOM CURSOR — BAT + WEB ──────────────── */
export const CustomCursor = () => {
  const [pos, setPos] = useState({ x: -300, y: -300 });
  const [hov, setHov] = useState(false);
  const [webs, setWebs] = useState([]);
  const isFine = typeof window !== 'undefined' && window.matchMedia('(pointer:fine)').matches;

  useEffect(() => {
    if (!isFine) return;
    document.body.style.cursor = 'none';

    const onMove = e => {
      setPos({ x: e.clientX, y: e.clientY });
      const el = document.elementFromPoint(e.clientX, e.clientY);
      setHov(!!(el && (el.tagName === 'BUTTON' || el.tagName === 'A'
        || el.closest('button') || el.closest('a'))));
    };

    const onClick = e => {
      const id = Date.now() + Math.random();
      setWebs(prev => [...prev, { id, x: e.clientX, y: e.clientY }]);
      setTimeout(() => setWebs(prev => prev.filter(w => w.id !== id)), 1400);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('click', onClick);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('click', onClick);
      document.body.style.cursor = '';
    };
  }, [isFine]);

  if (!isFine) return null;

  return (
    <>
      <style>{`
        @keyframes batFlap {
          0%,100% { transform:translate(-50%,-60%) scaleX(1) scaleY(1); }
          50%      { transform:translate(-50%,-60%) scaleX(.75) scaleY(.9); }
        }
        @keyframes batHover {
          0%,100% { transform:translate(-50%,-60%) scaleX(1.15) scaleY(1); }
          50%      { transform:translate(-50%,-60%) scaleX(.85) scaleY(1.1); }
        }
        @keyframes webAppear {
          0%   { transform:translate(-50%,-50%) scale(0) rotate(-20deg); opacity:0; }
          25%  { transform:translate(-50%,-50%) scale(1.1) rotate(5deg);  opacity:1; }
          80%  { opacity:.7; }
          100% { transform:translate(-50%,-50%) scale(1.4) rotate(15deg); opacity:0; }
        }
      `}</style>

      {/* Bat cursor */}
      <div style={{
        position: 'fixed', zIndex: 99999, pointerEvents: 'none',
        left: pos.x, top: pos.y,
        fontSize: hov ? 24 : 18,
        transition: 'font-size .2s ease',
        animation: hov ? 'batHover .35s ease-in-out infinite' : 'batFlap .55s ease-in-out infinite',
        filter: `drop-shadow(0 0 ${hov ? 14 : 8}px rgba(189,147,249,.9))
                 drop-shadow(0 0 ${hov ? 28 : 14}px rgba(255,121,198,.4))`,
        userSelect: 'none',
      }}>🦇</div>

      {/* Spider webs at click positions */}
      {webs.map(w => (
        <div key={w.id} style={{
          position: 'fixed', zIndex: 99998, pointerEvents: 'none',
          left: w.x, top: w.y,
          animation: 'webAppear 1.4s ease-out forwards',
        }}>
          <SpiderWeb size={90} />
        </div>
      ))}
    </>
  );
};

/* ─── SIDE NAV DOTS ──────────────────────────── */
export const SideNav = ({ sections, active }) => (
  <div className="side-nav" style={{
    position: 'fixed', right: 22, top: '50%', transform: 'translateY(-50%)',
    zIndex: 80, display: 'flex', flexDirection: 'column',
    gap: 18, alignItems: 'center',
  }}>
    {sections.map(s => (
      <a key={s.id} href={`#${s.id}`} title={s.label} style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none' }}>
        {/* Label (visible on hover) */}
        <span style={{
          color: active === s.id ? s.color : '#44475a',
          fontSize: 9, fontFamily: "'JetBrains Mono',monospace",
          letterSpacing: 1.5, textTransform: 'uppercase',
          opacity: active === s.id ? 0.9 : 0,
          transform: active === s.id ? 'translateX(0)' : 'translateX(8px)',
          transition: 'all .3s ease',
          whiteSpace: 'nowrap',
        }}>{s.label}</span>
        {/* Dot */}
        <div style={{
          width: active === s.id ? 10 : 5,
          height: active === s.id ? 10 : 5,
          borderRadius: '50%',
          background: active === s.id ? s.color : 'transparent',
          border: `1px solid ${active === s.id ? s.color : '#2a2d3a'}`,
          boxShadow: active === s.id ? `0 0 10px ${s.color}, 0 0 20px ${s.color}40` : 'none',
          transition: 'all .35s cubic-bezier(.16,1,.3,1)',
        }} />
      </a>
    ))}
    {/* Vertical connector line */}
    <div style={{
      position: 'absolute', right: 4, top: 0, bottom: 0, width: 1,
      background: 'linear-gradient(180deg,transparent,#1e2030 20%,#1e2030 80%,transparent)',
      zIndex: -1,
    }} />
  </div>
);

/* ─── GEOMETRIC DIVIDER ──────────────────────── */
export const GeoDivider = ({ color1 = '#bd93f9', color2 = '#ff79c6', flip = false }) => {
  const [vis, setVis] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const o = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVis(true); }, { threshold: 0.3 });
    if (ref.current) o.observe(ref.current);
    return () => o.disconnect();
  }, []);

  const pts = flip
    ? [[0,30],[150,50],[280,20],[420,42],[560,8],[700,38],[840,15],[980,45],[1100,25],[1200,30]]
    : [[0,30],[120,12],[250,45],[400,18],[540,40],[680,10],[800,42],[950,22],[1080,48],[1200,30]];

  const nodes = pts.slice(1, -1);

  return (
    <div ref={ref} style={{ position: 'relative', padding: '0 0', overflow: 'visible', zIndex: 10 }}>
      <svg viewBox="0 0 1200 60" style={{ width: '100%', display: 'block', height: 60 }} preserveAspectRatio="none">
        <defs>
          <linearGradient id={`gd${flip ? '1' : '0'}`} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="transparent" />
            <stop offset="20%" stopColor={color1} stopOpacity="0.4" />
            <stop offset="50%" stopColor={color2} stopOpacity="0.5" />
            <stop offset="80%" stopColor={color1} stopOpacity="0.4" />
            <stop offset="100%" stopColor="transparent" />
          </linearGradient>
          <filter id="glow">
            <feGaussianBlur stdDeviation="2" result="blur" />
            <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>
        {/* Main line */}
        <polyline
          points={pts.map(p => p.join(',')).join(' ')}
          fill="none"
          stroke={`url(#gd${flip ? '1' : '0'})`}
          strokeWidth="1"
          filter="url(#glow)"
          style={{
            strokeDasharray: 1600,
            strokeDashoffset: vis ? 0 : 1600,
            transition: 'stroke-dashoffset 1.8s cubic-bezier(.16,1,.3,1)',
          }}
        />
        {/* Nodes */}
        {nodes.map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r="3.5" fill={i % 2 === 0 ? color1 : color2}
            style={{
              opacity: vis ? 0.7 : 0,
              transition: `opacity .4s ease ${0.3 + i * 0.1}s`,
              filter: `drop-shadow(0 0 4px ${i % 2 === 0 ? color1 : color2})`,
            }}
          />
        ))}
        {/* Connecting verticals */}
        {nodes.filter((_, i) => i % 2 === 0).map(([x, y], i) => (
          <line key={i} x1={x} y1={y} x2={x} y2={y > 30 ? y - 12 : y + 12}
            stroke={color1} strokeWidth="0.8" strokeOpacity="0.3"
            style={{ opacity: vis ? 1 : 0, transition: `opacity .3s ease ${0.5 + i * 0.1}s` }}
          />
        ))}
      </svg>
    </div>
  );
};

/* ─── FLYING BAT ─────────────────────────────── */
export const FlyingBat = ({ delay = 0, startX = -50, startY = 80, size = 24 }) => {
  const [pos, setPos] = useState({ x: startX, y: startY });
  const frame = useRef(0);
  const baseX = useRef(startX);

  useEffect(() => {
    const timer = setTimeout(() => {
      const interval = setInterval(() => {
        frame.current += 0.02;
        const newX = baseX.current + Math.sin(frame.current * 1.5) * 120 + frame.current * 15;
        const newY = startY + Math.cos(frame.current * 2) * 40;
        if (newX > window.innerWidth + 100) { baseX.current = -100; frame.current = 0; }
        setPos({ x: newX, y: newY });
      }, 30);
      return () => clearInterval(interval);
    }, delay);
    return () => clearTimeout(timer);
  }, [delay, startY]);

  return (
    <div style={{
      position: 'fixed', left: pos.x, top: pos.y,
      fontSize: size, zIndex: 5, pointerEvents: 'none',
      transition: 'left .03s linear, top .03s linear',
      filter: 'drop-shadow(0 0 12px rgba(189,147,249,.7)) drop-shadow(0 0 25px rgba(255,121,198,.3))',
    }}>🦇</div>
  );
};

/* ─── CODE RAIN (kept for compatibility) ─────── */
export const CodeRain = () => null;
